import { useEffect, useRef, useState } from 'react';
import { useSessionStore } from '../stores/sessionStore';
import { useCampaignStore } from '../stores/campaignStore';
import { useCharacterStore } from '../stores/characterStore';
import { useChatStore } from '../stores/chatStore';
import { useTokenStore } from '../stores/tokenStore';
import { useCombatStore } from '../stores/combatStore';
import { useMapStore } from '../stores/mapStore';
import { useAudioStore } from '../stores/audioStore';
import { useBestiaryStore } from '../stores/bestiaryStore';
import { useMobStore } from '../stores/mobStore';
import { useEncounterStore } from '../stores/encounterStore';
import { useCampaignHistoryStore } from '../stores/campaignHistoryStore';
import { useInitiativeStore } from '../stores/initiativeStore';
import { useSoloGamemasterStore } from '../stores/soloGamemasterStore';
import { useTierStore } from '../stores/tierStore';
import { useJournalStore } from '../stores/journalStore';
import { useSessionPlanStore } from '../stores/sessionPlanStore';
import { useNpcMindStore } from '../stores/npcMindStore';
import { useModuleStore } from '../stores/moduleStore';
import { supabase } from '../lib/supabase';

/**
 * Supabase Sync Hook
 *
 * Handles all initialization and real-time sync for multiplayer:
 * - Creates/syncs user on first load
 * - Creates/joins default campaign
 * - Subscribes to Realtime for chat, tokens, and combat
 * - Auto-syncs characters when they change
 *
 * Performance: when currentCampaignId is already set (lobby entry),
 * all data loads (chat, characters, tokens, combat, map) run in
 * a single parallel batch instead of cascading through effect cycles.
 */

// Default campaign ID - all players join this
const DEFAULT_CAMPAIGN_NAME = 'Lost Mines of Phandelver';

export function useSupabaseSync() {
  const initializeUser = useSessionStore((s) => s.initializeUser);
  const userId = useSessionStore((s) => s.userId);
  const userName = useSessionStore((s) => s.userName);
  const isInitialized = useSessionStore((s) => s.isInitialized);

  const currentCampaignId = useCampaignStore((s) => s.currentCampaignId);
  const setCurrentCampaign = useCampaignStore((s) => s.setCurrentCampaign);
  const loadCampaigns = useCampaignStore((s) => s.loadCampaigns);
  const createCampaign = useCampaignStore((s) => s.createCampaign);
  const joinCampaign = useCampaignStore((s) => s.joinCampaign);
  const campaigns = useCampaignStore((s) => s.campaigns);

  const loadFromSupabase = useCharacterStore((s) => s.loadFromSupabase);
  const subscribeToCharacterRealtime = useCharacterStore((s) => s.subscribeToRealtime);
  const unsubscribeFromCharacterRealtime = useCharacterStore((s) => s.unsubscribeFromRealtime);

  const subscribeToRealtime = useChatStore((s) => s.subscribeToRealtime);
  const loadChatFromSupabase = useChatStore((s) => s.loadFromSupabase);
  const unsubscribeFromRealtime = useChatStore((s) => s.unsubscribeFromRealtime);
  const setCurrentUser = useChatStore((s) => s.setCurrentUser);

  // Token sync
  const loadTokensFromSupabase = useTokenStore((s) => s.loadFromSupabase);
  const subscribeToTokenRealtime = useTokenStore((s) => s.subscribeToRealtime);
  const unsubscribeFromTokenRealtime = useTokenStore((s) => s.unsubscribeFromRealtime);

  // Combat sync
  const loadCombatFromSupabase = useCombatStore((s) => s.loadFromSupabase);
  const subscribeToCombatRealtime = useCombatStore((s) => s.subscribeToRealtime);
  const unsubscribeFromCombatRealtime = useCombatStore((s) => s.unsubscribeFromRealtime);

  // Map sync
  const loadMapFromScene = useMapStore((s) => s.loadFromScene);

  const hasInitRef = useRef(false);
  const currentSceneIdRef = useRef<string | null>(null);
  const [isSceneReady, setIsSceneReady] = useState(false);

  // Step 1: Initialize user on mount
  useEffect(() => {
    if (hasInitRef.current) return;
    hasInitRef.current = true;

    const init = async () => {
      console.log('[Supabase] Initializing user...');
      await initializeUser();
    };

    init();
  }, [initializeUser]);

  // Step 2: Once user is ready, load/create campaign
  useEffect(() => {
    if (!userId || !isInitialized) return;

    const setupCampaign = async () => {
      console.log('[Supabase] Loading campaigns for user:', userId);
      await loadCampaigns(userId);
    };

    setupCampaign();
  }, [userId, isInitialized, loadCampaigns]);

  // Step 3: Create or join default campaign if none exists
  useEffect(() => {
    if (!userId || !isInitialized) return;
    // Skip if we already have a campaign selected
    if (currentCampaignId) return;

    const ensureCampaign = async () => {
      // Check if user has any campaigns
      if (campaigns.length > 0) {
        // Use the first one
        console.log('[Supabase] Using existing campaign:', campaigns[0].name);
        setCurrentCampaign(campaigns[0].id);
        return;
      }

      // Try to find and join the default campaign.
      // Under deny-by-default RLS a non-member CANNOT see machina_campaigns via a
      // direct SELECT (member-only policy), so discovery goes through the
      // SECURITY DEFINER lookup RPC machina_find_open_campaign, which returns the
      // id of the one shared, openly-joinable ('standard') campaign by name
      // without leaking the row. Without this, every new user would fail to find
      // the shared campaign and silently spawn their own duplicate.
      console.log('[Supabase] Looking for default campaign...');
      const { data: defaultCampaignId, error: lookupError } = await supabase.rpc(
        'machina_find_open_campaign',
        { p_name: DEFAULT_CAMPAIGN_NAME }
      );

      if (lookupError) {
        console.error('[Supabase] Default campaign lookup failed:', lookupError);
      }

      if (defaultCampaignId) {
        console.log('[Supabase] Joining existing campaign:', DEFAULT_CAMPAIGN_NAME);
        await joinCampaign(userId, defaultCampaignId as string);
      } else {
        // No shared default campaign exists yet — create it.
        console.log('[Supabase] Creating default campaign:', DEFAULT_CAMPAIGN_NAME);
        await createCampaign(userId, DEFAULT_CAMPAIGN_NAME, 'A classic tabletop RPG adventure');
      }
    };

    ensureCampaign();
  }, [userId, isInitialized, campaigns, currentCampaignId, createCampaign, joinCampaign, setCurrentCampaign]);

  // Step 4: Subscribe to Realtime and load ALL data when campaign is set.
  // This is the hot path — everything runs in one parallel batch.
  useEffect(() => {
    if (!currentCampaignId) return;

    const t0 = performance.now();
    console.log('[Supabase] Setting up campaign:', currentCampaignId);

    // Reset scene ready state when campaign changes
    setIsSceneReady(false);

    // =========================================================================
    // CAMPAIGN ISOLATION — clear ALL stale state from previous campaign.
    // Without this, switching campaigns can show characters, mobs, chat,
    // maps, sessions, and history from the previous campaign until fresh
    // data loads overwrite them.
    // =========================================================================
    useInitiativeStore.getState().clearAllEntries();
    useCombatStore.getState().clearAllInitiative();
    useEncounterStore.getState().endEncounter();
    useMobStore.getState().clearLocalState();
    useNpcMindStore.getState().clearAll();
    useMapStore.getState().clearCampaignState();
    useTokenStore.getState().clearLocalState();
    useChatStore.getState().clearLocalState();
    useBestiaryStore.getState().clearLocalState();
    useModuleStore.getState().loadModulesForCampaign(currentCampaignId);

    // Fire-and-forget: subscribe to realtime channels (no await needed)
    subscribeToRealtime(currentCampaignId);
    subscribeToCharacterRealtime(currentCampaignId);
    // Audio sync — players subscribe to GM's broadcast
    useAudioStore.getState().subscribeToAudio(currentCampaignId);
    // GM always broadcasts audio — previously only fired when FoleyBoard panel
    // mounted, meaning music played locally but players heard silence.
    const isGmForAudio = useCampaignStore.getState().getCurrentCampaign()?.role === 'dm';
    if (isGmForAudio) {
      useAudioStore.getState().startBroadcasting(currentCampaignId);
    }
    // Bestiary + encounter mobs realtime
    useBestiaryStore.getState().subscribeToRealtime(currentCampaignId);
    useMobStore.getState().subscribeToRealtime(currentCampaignId);
    // Campaign history realtime
    useCampaignHistoryStore.getState().subscribeToRealtime(currentCampaignId);
    if (userId) {
      setCurrentUser(userId);
    }

    // Main async setup — runs everything in parallel
    const setup = async () => {
      // Phase 1: Scene query + campaign-level data loads run in parallel
      const [sceneResult] = await Promise.all([
        // Scene query (needed for phase 2)
        supabase
          .from('machina_scenes')
          .select('id, name')
          .eq('campaign_id', currentCampaignId)
          .eq('is_active', true)
          .order('created_at', { ascending: false }),
        // Campaign-level loads (don't block scene setup)
        loadChatFromSupabase(currentCampaignId),
        userId ? loadFromSupabase(userId, currentCampaignId) : Promise.resolve(),
        userId ? useTierStore.getState().loadTier(userId) : Promise.resolve(),
        useBestiaryStore.getState().loadBestiary(currentCampaignId),
        useCampaignHistoryStore.getState().loadHistory(currentCampaignId),
        useMapStore.getState().loadSavedMaps(currentCampaignId),
        useJournalStore.getState().loadJournal(currentCampaignId),
        useSessionPlanStore.getState().loadFromDb(currentCampaignId),
        // Mobs now loaded in Phase 3 with sceneId to avoid orphaned ghost entries
      ]);

      const { data: scenes, error: sceneError } = sceneResult;
      if (sceneError) {
        console.error('[Supabase] Failed to query scenes:', sceneError);
      }

      // Deactivate stale scenes (fire-and-forget)
      if (scenes && scenes.length > 1) {
        const deactivateIds = scenes.slice(1).map(s => s.id);
        console.warn('[Supabase] Deactivating', deactivateIds.length, 'extra active scenes');
        supabase
          .from('machina_scenes')
          .update({ is_active: false })
          .in('id', deactivateIds)
          .then();
      }

      // Resolve scene ID
      let sceneId: string;

      if (scenes && scenes.length > 0) {
        sceneId = scenes[0].id;
        console.log('[Supabase] Using scene:', scenes[0].name, '(', sceneId, ')');
      } else {
        // Create default scene — handle race conditions via unique constraint
        const { data: newScene, error } = await supabase
          .from('machina_scenes')
          .insert({
            campaign_id: currentCampaignId,
            name: 'Main Scene',
            is_active: true,
            map_data: {},
          })
          .select('id')
          .single();

        if (error) {
          if (error.code === '23505') {
            const { data: existingScene } = await supabase
              .from('machina_scenes')
              .select('id, name')
              .eq('campaign_id', currentCampaignId)
              .eq('name', 'Main Scene')
              .eq('is_active', true)
              .single();

            if (existingScene) {
              sceneId = existingScene.id;
            } else {
              console.error('[Supabase] Failed to find scene after conflict');
              return;
            }
          } else {
            console.error('[Supabase] Failed to create scene:', error);
            return;
          }
        } else {
          sceneId = newScene.id;
          console.log('[Supabase] Created new scene:', sceneId);
        }
      }

      currentSceneIdRef.current = sceneId;
      // Single publisher for sceneId — every store that writes to DB gets it
      // here, before any subscriptions or loads fire. Eliminates the race
      // where writes fired against sceneId=null.
      useCombatStore.getState().setCurrentSceneId(sceneId);
      useTokenStore.getState().setCurrentSceneId(sceneId);
      useMapStore.getState().setCurrentSceneId(sceneId);

      // Phase 2: Scene-level realtime subscriptions (non-blocking)
      subscribeToTokenRealtime(sceneId);
      subscribeToCombatRealtime(sceneId);
      useEncounterStore.getState().subscribeToRealtime(sceneId, currentCampaignId);
      useMapStore.getState().subscribeToSceneMap(sceneId);
      useJournalStore.getState().subscribeToRealtime(currentCampaignId);

      // Phase 3: Scene-level data loads in parallel
      await Promise.all([
        loadTokensFromSupabase(sceneId),
        loadCombatFromSupabase(sceneId),
        useEncounterStore.getState().loadFromSupabase(sceneId, currentCampaignId),
        useMobStore.getState().loadMobs(currentCampaignId, sceneId),
        loadMapFromScene(sceneId).catch((err) => {
          console.warn('[Supabase] Map load failed (non-fatal):', err);
        }),
      ]);

      // Reconcile encounter participants against the now-hydrated character &
      // mob stores: drop orphans whose backing row no longer exists (foreign
      // cross-campaign chars stamped into participants, mobs whose rows were
      // cleared). Runs here, after Phase 3, so both stores are populated and
      // the scrub is safe. Fixes demo-party bleed + "No monsters" vs full
      // initiative desync.
      void useEncounterStore.getState().reconcileParticipants(currentCampaignId);

      // Phase 4: Solo mode init — fire-and-forget so the board renders
      // immediately. Blocking setIsSceneReady on the AI DM's round-trip kept
      // the loader up for the full LLM latency on every load. On resume the
      // server returns a cheap no-op (no LLM call); on a fresh start the
      // narration streams into chat while the board is already visible.
      const soloState = useSoloGamemasterStore.getState();
      if (soloState.isSoloMode && !soloState.isInitialized) {
        void soloState.initialize();
      }

      console.log('[Supabase] Setup complete in', Math.round(performance.now() - t0), 'ms');
      setIsSceneReady(true);
    };

    setup().catch((err) => {
      console.error('[Supabase] Setup failed:', err);
      // Still mark ready so the user isn't stuck on the loader forever
      setIsSceneReady(true);
    });

    return () => {
      unsubscribeFromRealtime();
      unsubscribeFromCharacterRealtime();
      unsubscribeFromTokenRealtime();
      unsubscribeFromCombatRealtime();
      useEncounterStore.getState().unsubscribeFromRealtime();
      useMapStore.getState().unsubscribeFromSceneMap();
      useAudioStore.getState().unsubscribeFromAudio();
      useBestiaryStore.getState().unsubscribeFromRealtime();
      useMobStore.getState().unsubscribeFromRealtime();
      useCampaignHistoryStore.getState().unsubscribeFromRealtime();
      useJournalStore.getState().unsubscribeFromRealtime();
    };
  }, [currentCampaignId, subscribeToRealtime, loadChatFromSupabase, loadFromSupabase, userId, unsubscribeFromRealtime, subscribeToCharacterRealtime, unsubscribeFromCharacterRealtime, subscribeToTokenRealtime, loadTokensFromSupabase, unsubscribeFromTokenRealtime, subscribeToCombatRealtime, loadCombatFromSupabase, unsubscribeFromCombatRealtime, setCurrentUser, loadMapFromScene]);

  return {
    userId,
    userName,
    currentCampaignId,
    currentSceneId: currentSceneIdRef.current,
    isReady: !!userId && !!currentCampaignId,
    isSceneReady,
  };
}
